// ============= Imports =============
import { SignalRContextProps } from '@/contexts/SignalRContext';

// ============= Constants =============
const HUB_NAME = 'userRelayHub';

// ============= Types =============
export interface SendMessageToUserUserSessionProps {
  invokeHub: SignalRContextProps['invokeHub'];
  /** Target user ID to send the message to */
  userId: string;
  /** Current user's ID (sender) */
  senderId: string;
  /** Message payload (e.g. { type: 'ARE_YOU_THERE', workspaceId } or { type: 'I_AM_HERE', workspaceId }) */
  message: Record<string, unknown> | string;
}

// ============= Main Function =============
/**
 * Sends a message to a specific user via UserRelayHub (SendMessageToUserAsync).
 * Used for presence discovery: sender sends ARE_YOU_THERE; recipient replies with I_AM_HERE.
 *
 * @param invokeHub Function to invoke SignalR hub methods
 * @param userId The ID of the user to send the message to
 * @param senderId The ID of the sender (current user)
 * @param message The message content (object or string)
 * @returns Promise with API response or error
 */
export const sendMessageToUserUserSession = async ({
  invokeHub,
  userId,
  senderId,
  message,
}: SendMessageToUserUserSessionProps): Promise<unknown> => {
  try {
    if (!userId) {
      throw new Error('userId is required');
    }
    if (!senderId) {
      throw new Error('senderId is required');
    }
    if (message === undefined || message === null) {
      throw new Error('message is required');
    }

    const result = await invokeHub<unknown>(
      HUB_NAME,
      'SendMessageToUserAsync',
      userId,
      senderId,
      typeof message === 'string' ? message : message
    );

    if (import.meta.env.DEV) {
      console.log(
        `✅ SendMessageToUserAsync to [${userId}] (${typeof message === 'object' && message && 'type' in message ? (message as { type: string }).type : 'message'})`
      );
    }
    return result;
  } catch (error) {
    console.error(
      `🔴 Error sending message to user [${userId}] via UserRelayHub:`,
      error
    );
    throw error;
  }
};
